import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import appVersionClient from '../../../../../../clients/app-version';

const FALLBACK_SCHEME = process.env.REACT_APP_DEEP_LINK_SCHEME || 'regionas';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 300,
  },
  qrContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    gap: theme.spacing(2),
  },
  qrCode: {
    padding: theme.spacing(2),
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  downloadButton: {
    marginTop: theme.spacing(1),
  },
  linkRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  linkShort: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    maxWidth: 280,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const buildSpendUrl = (scheme, objectDocumentId, codeDocumentId) => {
  if (!objectDocumentId || !codeDocumentId) return '';
  return `${scheme}://coins/spend?objectDocumentId=${encodeURIComponent(objectDocumentId)}&codeDocumentId=${encodeURIComponent(codeDocumentId)}`;
};

const SpendQRCodeDialog = ({
  open,
  onClose,
  objectDocumentId,
  codeDocumentId,
  name,
}) => {
  const classes = useStyles();
  const qrRef = useRef(null);
  const [appSchema, setAppSchema] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    appVersionClient
      .getAppVersion()
      .then((res) => {
        if (!cancelled && res?.schema) setAppSchema(res.schema);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const scheme = appSchema || FALLBACK_SCHEME;
  const spendUrl = buildSpendUrl(scheme, objectDocumentId, codeDocumentId);
  const shortLabel = spendUrl ? `${scheme}://coins/spend?...` : '';

  const handleCopyLink = () => {
    if (!spendUrl) return;
    navigator.clipboard?.writeText(spendUrl).catch(() => {});
  };

  const handleDownload = () => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const padding = 20;
    const size = 256 + padding * 2;
    canvas.width = size;
    canvas.height = size;

    img.onload = () => {
      try {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, padding, padding, 256, 256);
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download =
              `qr-spend-${name || codeDocumentId || 'code'}.png`.replace(
                /[^a-zA-Z0-9.-]/g,
                '-',
              );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }, 'image/png');
      } catch (error) {
        console.error('Error generating PNG:', error);
      }
    };
    img.onerror = () => console.error('Error loading SVG image');
    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8',
    });
    img.src = URL.createObjectURL(svgBlob);
    setTimeout(() => URL.revokeObjectURL(img.src), 1000);
  };

  if (!spendUrl) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        {name ? `QR: ${name}` : 'QR kodas (spend)'}
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className={classes.qrContainer} ref={qrRef}>
          <Paper className={classes.qrCode} elevation={2}>
            <QRCodeSVG value={spendUrl} size={256} level="H" includeMargin />
          </Paper>
          <div className={classes.linkRow}>
            <Typography
              variant="body2"
              className={classes.linkShort}
              title={spendUrl}
            >
              {shortLabel}
            </Typography>
            <Tooltip title="Kopijuoti nuorodą">
              <IconButton
                size="small"
                onClick={handleCopyLink}
                aria-label="Kopijuoti nuorodą"
              >
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDownload}
          variant="contained"
          color="primary"
          startIcon={<GetAppIcon />}
          className={classes.downloadButton}
        >
          Atsisiųsti PNG
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SpendQRCodeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  objectDocumentId: PropTypes.string,
  codeDocumentId: PropTypes.string,
  name: PropTypes.string,
};

export default SpendQRCodeDialog;
export { buildSpendUrl };
